import {Component, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {filter, takeUntil} from 'rxjs/operators';

import {PayTypeSelector, PayTypeWithCliRegIds} from '../../../../+payrolls/payrolls.model';
import {PayrollUtilsService} from '../../../../+payrolls/utils/payroll-utils.service';
import {Destroyable} from '../../../../destroyable.component';
import {EmployeesContextUtilsService} from '../../../../shared/employees/utils/employee-context-utils.service';

@Component({
  selector: 'gp-context-pay-type',
  templateUrl: './context-pay-type.component.html'
})
export class ContextPayTypeComponent extends Destroyable implements OnInit {

  @Input()
  public forceDisplayValue: string = '';

  public payTypes: PayTypeSelector[] = [];
  public payTypesMap: Map<string, PayTypeSelector[]> = new Map();
  public selectedPayType: PayTypeSelector;

  constructor(private payrollUtilsService: PayrollUtilsService,
              private translate: TranslateService,
              private employeesContextUtilsService: EmployeesContextUtilsService) {
    super();
    this.customPlaceholderGenerator = this.customPlaceholderGenerator.bind(this);
  }

  ngOnInit() {
    this.fetchPayTypes();

    this.employeesContextUtilsService.contextPayType$
      .pipe(
        filter(payType => payType != null),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((payTypeSelector: PayTypeSelector) => {
        let newPayType = payTypeSelector;
        if (payTypeSelector !== 'ALL' && typeof payTypeSelector === 'string') {
          for (const payType of this.payTypes) {
            if ((payType as PayTypeWithCliRegIds).transId === payTypeSelector) {
              newPayType = payType;
            }
          }
        }
        if (this.selectedPayType !== newPayType) {
          this.selectedPayType = newPayType;
        }
      });
  }

  public customPlaceholderGenerator(payType: PayTypeSelector, transId: string): string {
    if (payType === 'ALL') {
      return this.translate.instant('CONTEXT_BAR.all');
    } else {
      return this.translate.instant(transId);
    }
  }

  public selectPayType(payType: PayTypeSelector) {
    this.employeesContextUtilsService.contextPayType$.next(payType);
  }

  public displayPayTypeText(payType: PayTypeSelector): string {
    if (payType === 'ALL') {
      return 'CONTEXT_BAR.working_on_pay_type_all';
    } else {
      return (payType as PayTypeWithCliRegIds).transId;
    }
  }

  // keyValuePipe require a sort function, but we don't want to change order as it's already done
  public keepOrder(a, b) {
    return a;
  }

  private selectPayTypeByName(payTypeName: string) {
    for (const payType of this.payTypes) {
      if ((payType as PayTypeWithCliRegIds).name === payTypeName) {
        this.selectPayType(payType);
        return;
      }
    }
  }

  private fetchPayTypes() {
    this.payrollUtilsService.payTypesSorted$
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(response => response !== null)
      )
      .subscribe((payTypes: PayTypeWithCliRegIds[]) => {
        this.payTypes = [
          'ALL',
          ...payTypes
        ];

        this.createMapPayType();

        if (!this.selectedPayType) {
          const storedContextPayType = this.employeesContextUtilsService.getStoredContextPayType();
          if (storedContextPayType) {
            this.selectPayTypeByName(storedContextPayType.transId);
          } else {
            this.setDefaultPayType();
          }
        }
      });
  }

  private setDefaultPayType(): void {
    this.selectedPayType = 'ALL';
  }

  private createMapPayType(): void {

    this.payTypesMap.clear();

    this.payTypes.forEach((payType: PayTypeSelector) => {
      if (payType === 'ALL') {
        this.payTypesMap.set('ALL', ['ALL']);
      } else if (this.payrollUtilsService.isPayType(payType)) {

        const payTypeItem = payType as PayTypeWithCliRegIds;
        if (!this.payTypesMap.has(payTypeItem.calculationType)) {
          this.payTypesMap.set(payTypeItem.calculationType, []);
        }
        this.payTypesMap.get(payTypeItem.calculationType).push(payTypeItem);
      }
    });
  }
}
